/**
 * Frontend exception tracking (Sentry). Mirrors apps/api/internal/sentryx:
 * nil-degrades when VITE_SENTRY_DSN is unset (no project exists yet — see
 * #101), and applies the same explicit PII scrubbing on top of the SDK's
 * own defaults, since this is a KYC/compliance product and applicant
 * emails, document contents, and form field values must never leave the
 * browser in a Sentry payload.
 *
 * The SDK's own defaults already help: no automatic request/response body
 * capture, and `sendDefaultPii` (equivalent to the Go SDK's
 * SendDefaultPII) defaults to false, so it never auto-attaches cookies or
 * IP addresses. What it can't know is which application-specific fields
 * (form values, extracted document fields, applicant email) carry PII —
 * beforeSend/beforeBreadcrumb below close that gap.
 */
import * as Sentry from '@sentry/react';

const SENSITIVE_KEY_TERMS = [
  'email',
  'e-mail',
  'filename',
  'file_name',
  'filecontent',
  'file_content',
  'document',
  'content',
  'body',
  'ocr',
  'extract',
  'form_data',
  'form_snapshot',
  'snapshot',
  'field_value',
  'first_name',
  'firstname',
  'last_name',
  'lastname',
  'full_name',
  'fullname',
  'legal_name',
  'company_name',
  'address',
  'postcode',
  'postal',
  'phone',
  'dob',
  'birth',
  'ssn',
  'passport',
  'national_id',
  'nationality',
  'tax_id',
  'iban',
  'account_number',
  'auth',
  'token',
  'session',
  'password',
  'secret',
  'credential',
  'signature',
];

const SAFE_METADATA_KEYS = new Set([
  'application_id',
  'attempt_id',
  'request_id',
  'status',
  'step',
  'method',
  'path',
  'slot',
  'owner_index',
  // React's error-boundary context (SAFE_CONTEXTS' 'react' entry): a
  // component-name call stack with no prop values, unlike everything else
  // in this allowlist which is applicant/request metadata.
  'componentstack',
]);

const SAFE_CONTEXTS = new Set(['trace', 'react']);

const REDACTED = '[Filtered]';

const EMAIL_PATTERN = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

export function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  return SENSITIVE_KEY_TERMS.some((term) => lower.includes(term));
}

export function scrubString(value: string): string {
  return value.replace(EMAIL_PATTERN, REDACTED);
}

/**
 * Recursively scrubs an arbitrary JSON-ish value, returning a new value.
 *
 * Object keys are allowlisted against SAFE_METADATA_KEYS, the same list
 * event.tags is gated on below — not denylisted via isSensitiveKey alone.
 * A denylist only catches keys that happen to look sensitive by name, and
 * a future nested field under SAFE_CONTEXTS has no reason to coincidentally
 * match one; anything not explicitly known-safe is redacted instead. The
 * top-level value itself (not a key) is exempt, since scrubValue is also
 * called directly on values with no key of their own.
 */
function scrubValue(value: unknown, seen = new WeakSet<object>()): unknown {
  if (typeof value === 'string') return scrubString(value);
  if (value === null || typeof value !== 'object') return value;
  // Returning the original cyclic object would bypass all scrubbing when the
  // SDK later normalized it. Redact the repeated reference instead.
  if (seen.has(value as object)) return REDACTED;
  seen.add(value as object);

  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item, seen));
  }
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    if (!SAFE_METADATA_KEYS.has(key.toLowerCase()) || isSensitiveKey(key)) {
      out[key] = REDACTED;
      continue;
    }
    out[key] = scrubValue(val, seen);
  }
  return out;
}

function scrubRequest(request: Sentry.Event['request']): Sentry.Event['request'] {
  if (!request) return request;
  // Request bodies, query strings, cookies, headers, and URLs can all contain
  // arbitrary applicant input. Only the method is required to diagnose which
  // operation failed; discard every other request field.
  return {
    method: request.method,
  };
}

export function beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  // Error text can interpolate a form value or rejected document content.
  // Keep stack/type for grouping, but never transmit free-form messages.
  if (event.message) event.message = REDACTED;
  if (event.request) event.request = scrubRequest(event.request);
  event.user = undefined;
  event.extra = undefined;
  event.fingerprint = undefined;
  if (event.contexts) {
    const safeContexts: typeof event.contexts = {};
    for (const [key, value] of Object.entries(event.contexts)) {
      if (SAFE_CONTEXTS.has(key)) {
        safeContexts[key] = scrubValue(value) as (typeof event.contexts)[string];
      }
    }
    event.contexts = safeContexts;
  }
  if (event.tags) {
    for (const [key, val] of Object.entries(event.tags)) {
      if (!SAFE_METADATA_KEYS.has(key.toLowerCase()) || isSensitiveKey(key)) {
        event.tags[key] = REDACTED;
      } else if (typeof val === 'string') {
        event.tags[key] = scrubString(val);
      }
    }
  }
  if (event.exception?.values) {
    for (const exc of event.exception.values) {
      if (exc.value) exc.value = REDACTED;
      if (exc.mechanism) exc.mechanism.data = undefined;
      if (exc.stacktrace?.frames) {
        for (const frame of exc.stacktrace.frames) {
          // Browser SDKs normally omit locals, but the event protocol permits
          // them. Clear explicitly so future integrations cannot enable them.
          frame.vars = undefined;
        }
      }
    }
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
  }
  return event;
}

export function scrubBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb {
  // Console/request/navigation breadcrumbs can contain arbitrary objects,
  // response bodies, form values, and query strings. Preserve only metadata
  // useful for chronology; never their free-form payload.
  return {
    category: breadcrumb.category,
    level: breadcrumb.level,
    timestamp: breadcrumb.timestamp,
    type: breadcrumb.type,
  };
}

/**
 * Initializes Sentry once at app startup (see main.tsx). A no-op when
 * VITE_SENTRY_DSN is unset — every other Sentry.* call in the app (the
 * ErrorBoundary in App.tsx) degrades to a plain fallback UI in that case,
 * same nil-degrades pattern as the backend.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    // Not set: sendDefaultPii defaults to false — no automatic cookies, no
    // automatic IP capture. Never flip this to true; see the module doc
    // comment.
    integrations: [
      // Explicitly excludes Sentry.browserTracingIntegration() (no
      // performance/tracing) and Sentry.replayIntegration() (no session
      // replay, which would otherwise record on-screen form input) — this
      // is exception tracking only, matching the API side's scope and
      // keeping event volume within the free tier (#101 acceptance
      // criteria).
    ],
    beforeSend,
    beforeBreadcrumb: scrubBreadcrumb,
  });
}
